import { useState } from 'react';

import { Radio, RadioGroup } from '@headlessui/react';

import { Page } from '@/components/layout/Page';
import type { ShowColumns, OptimizeMode, PolicyVariantType, Policy } from '@/features/decisions/types';

const CASH_COLS = [
  { abbr: 'cM', title: 'Material Cost', unit: 'per unit' },
  { abbr: 'cV', title: 'Variable Conversion', unit: 'per unit' },
  { abbr: 'cF', title: 'Fixed Conversion', unit: 'per run' },
  { abbr: 'p', title: 'Cash Collected', unit: 'per unit' },
  { abbr: 'v', title: 'In-Stock Value', unit: 'per unit' },
  { abbr: 's', title: 'Salvage', unit: 'per unit' },
  { abbr: 'd', title: 'Disposal', unit: 'per unit' },
] as const;

const CONSTRAINT_COLS = [
  { abbr: 'SL', title: 'Service Level', subtitle: 'Availability target' },
  { abbr: 'LT', title: 'Production Lead Time', subtitle: 'Decision → availability' },
  { abbr: 'BS', title: 'Batch Sizes', subtitle: 'Min / multiple lot' },
  { abbr: 'PR', title: 'Production Restr.', subtitle: 'Capacity / window' },
  { abbr: 'RR', title: 'Review Restr.', subtitle: 'When cycles change' },
] as const;

const RULE_BASED: Policy[] = [
  {
    id: 'lfl',
    name: 'Lot-for-lot (demand driven net requirement)',
    when: { type: 'FLOATING', description: 'Produce when new demand requirement is received.' },
    howMuch: { type: 'FLOATING', description: 'q = demand requirement – net inventory.' },
  },
  {
    id: 'foq',
    name: 'Fixed order quantity (FOQ)',
    when: {
      type: 'FLOATING',
      description: 'Timing is triggered or fixed — e.g. by periodic review of a reorder point.',
    },
    howMuch: { type: 'FIXED', description: 'q = Q fixed.' },
  },
  {
    id: 'fpc',
    name: 'Fixed production cycle, demand driven Q',
    badge: 'INCUMBENT',
    when: {
      type: 'FIXED',
      description: 'Order every n weeks (fixed), or on specific fixed dates (date_1, date_2, ...).',
    },
    howMuch: {
      type: 'FLOATING',
      description:
        'q = net requirement through the next production date: q = S – net inventory, or q = forecast demand – net inventory.',
    },
  },
  {
    id: 'rop',
    name: 'Reorder point with fixed order quantity (s, Q)',
    when: { type: 'FLOATING', description: 'Continuous / periodic review; order when net inventory ≤ s.' },
    howMuch: { type: 'FIXED', description: 'q = fixed Q (e.g. economic order quantity).' },
  },
];

const KEYSTONE: Policy = {
  id: 'keystone',
  name: 'Keystone',
  when: { type: 'OPTIMAL', description: "Optimal production cycle — subject to the SKU's cash inputs." },
  howMuch: { type: 'OPTIMAL', description: "Optimal quantity — subject to the SKU's cash inputs." },
};

const OPTIMIZE_OPTIONS: { value: OptimizeMode; label: string }[] = [
  { value: 'quantity-only', label: 'Quantity only' },
  { value: 'cycle-only', label: 'Cycle only' },
  { value: 'both', label: 'Both' },
];

const SHOW_COLUMN_OPTIONS: { value: ShowColumns; label: string; dotColor: string; textColor: string }[] = [
  { value: 'forecast', label: 'Forecast', dotColor: 'bg-gray-700', textColor: 'text-gray-700' },
  { value: 'cash-inputs', label: 'Cash inputs', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  { value: 'constraints', label: 'Constraints', dotColor: 'bg-amber-500', textColor: 'text-amber-500' },
];

function TypeTag({ type, active = true }: { type: PolicyVariantType; active?: boolean }) {
  if (type === 'FLOATING') return <span className="text-[11px] font-bold text-amber-500">FLOATING</span>;
  if (type === 'FIXED') return <span className="text-[11px] font-bold text-gray-400">FIXED</span>;
  return (
    <span
      className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-bold text-white transition-opacity ${
        active ? 'bg-[#1E3A5F]' : 'bg-[#1E3A5F] opacity-25'
      }`}
    >
      OPTIMAL
    </span>
  );
}

export function PoliciesMatrix() {
  const [showColumns, setShowColumns] = useState<ShowColumns | null>(null);
  const [optimize, setOptimize] = useState<OptimizeMode>('both');

  const extraCols =
    showColumns === 'forecast'
      ? 1
      : showColumns === 'cash-inputs'
        ? CASH_COLS.length
        : showColumns === 'constraints'
          ? CONSTRAINT_COLS.length
          : 0;
  const totalCols = 3 + extraCols;

  const toggleColumn = (val: ShowColumns) => setShowColumns((prev) => (prev === val ? null : val));

  return (
    <Page>
      <div className="rounded-xl border border-[#E4E0D8] bg-white">
        {/* Card header */}
        <div className="flex items-center justify-between border-b border-[#E4E0D8] px-6 py-4">
          <h3 className="text-sm font-semibold text-[#1A1C2E]">Production Planning Policies</h3>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#9295A5]">Show columns:</span>
            <div className="flex items-center gap-4">
              {SHOW_COLUMN_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleColumn(opt.value)}
                  className="flex items-center gap-1.5 focus:outline-none"
                >
                  <div
                    className={[
                      'h-3 w-3 flex-shrink-0 rounded-full border transition-colors',
                      showColumns === opt.value ? `border-transparent ${opt.dotColor}` : 'border-gray-400 bg-white',
                    ].join(' ')}
                  />
                  <span className={`text-xs ${showColumns === opt.value ? opt.textColor : 'text-[#6B7280]'}`}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Legend pills */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[#E4E0D8] px-6 py-3">
          <span className="text-[11px] font-bold text-amber-500">FLOATING</span>
          <span className="text-[11px] font-bold text-gray-400">FIXED</span>
          <span className="rounded-full bg-[#1E3A5F] px-2.5 py-0.5 text-[11px] font-bold text-white">OPTIMAL</span>

          {showColumns === 'forecast' && (
            <>
              <span className="rounded-full border border-gray-300 px-2.5 py-0.5 text-[10px] font-semibold text-gray-500">
                MEAN ONLY
              </span>
              <span className="rounded-full bg-violet-600 px-2.5 py-0.5 text-[10px] font-bold text-white">
                FULL DISTRIBUTION
              </span>
              <span className="text-[11px] text-[#9295A5]">demand input</span>
            </>
          )}

          {showColumns === 'cash-inputs' && (
            <>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-600 text-[10px] font-bold text-white">
                p
              </span>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-emerald-400 text-[10px] font-bold text-emerald-600">
                p
              </span>
              <span className="text-[11px] text-[#9295A5]">cash priced / not</span>
            </>
          )}

          {showColumns === 'constraints' && (
            <>
              <span className="inline-flex min-w-[20px] items-center justify-center rounded bg-amber-400 px-1 py-0.5 text-[10px] font-bold text-white">
                SL
              </span>
              <span className="text-[11px] text-[#9295A5]">bounds every policy</span>
            </>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-[#E4E0D8]">
                <th className="w-72 px-6 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]">
                  Production / Replenishment Policy
                </th>
                <th className="px-6 py-3">
                  <span className="block text-sm font-semibold text-[#1A1C2E]">When</span>
                  <span className="text-[11px] text-[#9295A5]">Timing — when to produce</span>
                </th>
                <th className="px-6 py-3">
                  <span className="block text-sm font-semibold text-[#1A1C2E]">How much</span>
                  <span className="text-[11px] text-[#9295A5]">Quantity — how much to produce</span>
                </th>

                {showColumns === 'forecast' && (
                  <th className="px-3 py-3 text-center">
                    <span className="block text-xs font-bold text-violet-600">F</span>
                    <span className="block text-xs font-semibold text-[#1A1C2E]">Forecast</span>
                    <span className="block text-[10px] text-[#9295A5]">Demand input</span>
                  </th>
                )}

                {showColumns === 'cash-inputs' &&
                  CASH_COLS.map((col) => (
                    <th key={col.abbr} className="px-2 py-3 text-center">
                      <span className="block text-[11px] font-bold text-emerald-600">{col.abbr}</span>
                      <span className="block text-[11px] font-semibold text-[#1A1C2E]">{col.title}</span>
                      <span className="block text-[10px] text-[#9295A5]">{col.unit}</span>
                    </th>
                  ))}

                {showColumns === 'constraints' &&
                  CONSTRAINT_COLS.map((col) => (
                    <th key={col.abbr} className="px-2 py-3 text-center">
                      <span className="block text-[11px] font-bold text-amber-500">{col.abbr}</span>
                      <span className="block text-[11px] font-semibold text-[#1A1C2E]">{col.title}</span>
                      <span className="block text-[10px] text-[#9295A5]">{col.subtitle}</span>
                    </th>
                  ))}
              </tr>
            </thead>

            <tbody>
              {/* Section 1 */}
              <tr>
                <td
                  colSpan={totalCols}
                  className="bg-[#FAFAF8] px-6 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]"
                >
                  Run Today · Rule-Based, No Solver
                </td>
              </tr>

              {RULE_BASED.map((policy) => (
                <tr key={policy.id} className="border-b border-[#F3F2EE]">
                  <td className="px-6 py-4 align-top">
                    {policy.badge && (
                      <span className="mb-1.5 inline-block rounded bg-[#1A1C2E] px-2 py-0.5 text-[9px] font-bold uppercase text-white">
                        {policy.badge}
                      </span>
                    )}
                    <p className={`text-sm font-semibold text-[#1A1C2E] ${policy.badge ? 'mt-1' : ''}`}>
                      {policy.name}
                    </p>
                  </td>
                  <td className="px-6 py-4 align-top">
                    <TypeTag type={policy.when.type} />
                    <p className="mt-1 text-xs text-[#6B7280]">{policy.when.description}</p>
                  </td>
                  <td className="px-6 py-4 align-top">
                    <TypeTag type={policy.howMuch.type} />
                    <p className="mt-1 text-xs text-[#6B7280]">{policy.howMuch.description}</p>
                  </td>

                  {showColumns === 'forecast' && (
                    <td className="px-3 py-4 text-center align-top">
                      <span className="inline-flex items-center rounded border border-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                        MEAN ONLY
                      </span>
                    </td>
                  )}

                  {showColumns === 'cash-inputs' &&
                    CASH_COLS.map((col) => (
                      <td key={col.abbr} className="px-2 py-4 text-center align-top">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded border border-emerald-300 text-[10px] font-bold text-emerald-600">
                          {col.abbr}
                        </span>
                      </td>
                    ))}

                  {showColumns === 'constraints' &&
                    CONSTRAINT_COLS.map((col) => (
                      <td key={col.abbr} className="px-2 py-4 text-center align-top">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded bg-amber-400 text-[10px] font-bold text-white">
                          {col.abbr}
                        </span>
                      </td>
                    ))}
                </tr>
              ))}

              {/* Section 2 */}
              <tr>
                <td
                  colSpan={totalCols}
                  className="bg-[#FAFAF8] px-6 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]"
                >
                  Keystone Optimizer · Solver-Based
                </td>
              </tr>

              {/* Keystone content row */}
              <tr>
                <td className="px-6 py-4 align-top">
                  <p className="text-sm font-semibold text-[#1A1C2E]">{KEYSTONE.name}</p>
                </td>
                <td className="px-6 py-4 align-top">
                  <TypeTag type={KEYSTONE.when.type} active={optimize === 'cycle-only' || optimize === 'both'} />
                  <p className="mt-2 text-xs text-[#6B7280]">{KEYSTONE.when.description}</p>
                </td>
                <td className="px-6 py-4 align-top">
                  <TypeTag type={KEYSTONE.howMuch.type} active={optimize === 'quantity-only' || optimize === 'both'} />
                  <p className="mt-2 text-xs text-[#6B7280]">{KEYSTONE.howMuch.description}</p>
                </td>

                {showColumns === 'forecast' && (
                  <td className="px-3 py-4 text-center align-top">
                    <span className="inline-flex items-center rounded-full border-2 border-violet-500 px-2 py-0.5 text-[10px] font-bold text-violet-600">
                      FULL DISTRIBUTION
                    </span>
                  </td>
                )}

                {showColumns === 'cash-inputs' &&
                  CASH_COLS.map((col) => (
                    <td key={col.abbr} className="px-2 py-4 text-center align-top">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-emerald-600 text-[10px] font-bold text-white">
                        {col.abbr}
                      </span>
                    </td>
                  ))}

                {showColumns === 'constraints' &&
                  CONSTRAINT_COLS.map((col) => (
                    <td key={col.abbr} className="px-2 py-4 text-center align-top">
                      <span
                        className={`inline-flex h-9 w-9 items-center justify-center rounded text-[10px] font-bold ${
                          col.abbr === 'SL' ? 'border border-amber-400 text-amber-500' : 'bg-amber-400 text-white'
                        }`}
                      >
                        {col.abbr}
                      </span>
                    </td>
                  ))}
              </tr>

              {/* Keystone optimize row — spans When + How much */}
              <tr>
                <td />
                <td colSpan={2} className="px-6 pb-5">
                  <RadioGroup value={optimize} onChange={setOptimize} className="flex items-center gap-2">
                    <span className="text-xs text-[#9295A5]">Optimize:</span>
                    <div className="flex divide-x divide-gray-200 overflow-hidden rounded-md border border-gray-200">
                      {OPTIMIZE_OPTIONS.map((opt) => (
                        <Radio
                          key={opt.value}
                          value={opt.value}
                          className={[
                            'cursor-pointer px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none',
                            optimize === opt.value
                              ? 'bg-[#1E3A5F] text-white'
                              : 'bg-white text-[#6B7280] hover:bg-gray-50',
                          ].join(' ')}
                        >
                          {opt.label}
                        </Radio>
                      ))}
                    </div>
                  </RadioGroup>
                </td>
                {showColumns === 'forecast' && <td />}
                {showColumns === 'cash-inputs' && CASH_COLS.map((col) => <td key={col.abbr} />)}
                {showColumns === 'constraints' && CONSTRAINT_COLS.map((col) => <td key={col.abbr} />)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
}
